import { useEffect, useRef, useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { useWindowSize } from "../../core";
import { Add, Search, Start } from "@mui/icons-material";
import { UnidadeSensivel } from "../zas";
import { IconFromStyle, PaintMarker, StyleButton } from "../mapa";
import { LatLng } from "leaflet";
import { RemoveButton } from "../modals";

interface TableUnidadesSensiveisProps {
  initialUnidades?: UnidadeSensivel[];
  isAdd?: boolean;
  isEditable?: boolean;
  isFiltrable?: boolean;
  onChange?: (edificacoes: UnidadeSensivel[]) => void;
  onChoose?: (edificacao: UnidadeSensivel) => void;
}

export const TableUnidadesSensiveis: React.FC<TableUnidadesSensiveisProps> = ({
  onChange,
  onChoose,
  isAdd,
  isEditable,
  isFiltrable,
  initialUnidades,
}) => {
  const { geometries } = useTheme().palette;
  const { height, width } = useWindowSize();

  const divRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const changeTimer = useRef<NodeJS.Timeout | null>(null);

  const [size, setSize] = useState({ width: 0, height: 0 });
  const [filterSize, setFilterSize] = useState({ width: 0, height: 0 });
  const [titleSize, setTitleSize] = useState({ width: 0, height: 0 });

  const [name, setName] = useState<string | undefined>();
  const [edificacoes, setEdificacoes] = useState<UnidadeSensivel[]>(initialUnidades ?? []);
  const [newEdificacao, setNewEdificacao] = useState<{
    nome: string;
    lat: number | string;
    lng: number | string;
    alt?: number | string;
    style: PaintMarker;
  }>({
    nome: "",
    lat: 0,
    lng: 0,
    style: geometries.uniSensivel,
  });


  useEffect(() => {
    if (divRef.current) {
      const { width, height } = divRef.current.getBoundingClientRect();
      setSize({ width, height });
    }
    if (filterRef.current) {
      const { width, height } = filterRef.current.getBoundingClientRect();
      setFilterSize({ width, height });
    }
    if (titleRef.current) {
      const { width, height } = titleRef.current.getBoundingClientRect();
      setTitleSize({ width, height });
    }
  }, [divRef, filterRef, titleRef, height, width]);

  useEffect(() => {
    if (changeTimer.current) clearTimeout(changeTimer.current);
    changeTimer.current = setTimeout(() => {
      //
    }, 500);

    return () => {
      if (changeTimer.current) clearTimeout(changeTimer.current);
    };
  }, [name]);

  const handleAddEdificacao = () => {
    if (
      newEdificacao.nome === "" ||
      (
        typeof newEdificacao.lat === "string" ||
        typeof newEdificacao.lng === "string" ||
        typeof newEdificacao.alt === "string"
      ) ||
      (newEdificacao.lat === 0 && newEdificacao.lng === 0)
    ) return;

    const edificacao = new UnidadeSensivel(
      new LatLng(newEdificacao.lat, newEdificacao.lng, newEdificacao.alt),
      {
        id: 0,
        lat: newEdificacao.lat,
        lon: newEdificacao.lng,
        alt: newEdificacao.alt,
        nome: newEdificacao.nome,
        style: newEdificacao.style,
        dataCadastro: new Date(Date.now()),
      }
    );

    const newEdificacoes = [...edificacoes];
    newEdificacoes.push(edificacao);
    if (onChange) onChange(newEdificacoes);
    setEdificacoes(newEdificacoes);
    setNewEdificacao({ nome: "", lat: 0, lng: 0, style: geometries.uniSensivel });
  };

  const onRemove = (unidade: UnidadeSensivel) => {
    const newEdificacoes = edificacoes.filter((ed) => ed !== unidade);
    setEdificacoes([...newEdificacoes]);
    if (onChange) onChange(newEdificacoes);
  };

  return <Box
    ref={divRef}
    sx={{
      flex: 1,
      width: "100%",
      height: "100%",
      padding: "15px",
      display: "flex",
      alignItems: "center",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    {/* Título */}
    <Box
      ref={titleRef}
      sx={{
        width: "100%",
        paddingTop: "7px",
        paddingX: "15px",
        display: "flex",
        flexDirection: "row",
        alignItems: "start",
        justifyContent: "start",
      }}>
      <Typography fontSize={18} fontWeight={"bold"}>
        {"Unidades Sensíveis na ZAS"}
      </Typography>
    </Box>

    {/* Filtros por nome e nova edificação*/}
    <div
      ref={filterRef}
      style={{ width: "100%" }}
    >
      <Box sx={{
        flex: 1,
        paddingX: "15px",
        gap: "15px",
        display: "flex",
        flexDirection: "column",
      }}>

        {/* Nova edificação */}
        {isAdd &&
          <Box sx={{
            gap: "15px",
            padding: "15px",
            display: "flex",
            flexDirection: "row",
          }}>

            {/* Name */}
            <TextField
              fullWidth
              label="Novo Nome"
              name="nome"
              value={newEdificacao.nome}
              onChange={(event) => {
                setNewEdificacao({
                  ...newEdificacao,
                  nome: event.target.value
                });
              }}
              variant="standard"
            />

            {/* Lat */}
            <TextField
              fullWidth
              label="Latitude"
              name="lat"
              value={newEdificacao.lat.toString().replaceAll(".", ",")}
              onChange={(event) => {
                const strVal = event.target.value.replaceAll(",", ".");

                let val: number | string = strVal;
                if (strVal !== "" && strVal !== "-" && !strVal.endsWith(".")) {
                  val = Number(strVal);
                  if (isNaN(val)) return;
                }
                setNewEdificacao({
                  ...newEdificacao,
                  lat: val,
                });
              }}
              variant="standard"
            />

            {/* Lng */}
            <TextField
              fullWidth
              label="Longitude"
              name="lng"
              value={newEdificacao.lng.toString().replaceAll(".", ",")}
              onChange={(event) => {
                const strVal = event.target.value.replaceAll(",", ".");

                let val: number | string = strVal;
                if (strVal !== "" && strVal !== "-" && !strVal.endsWith(".")) {
                  val = Number(strVal);
                  if (isNaN(val)) return;
                }
                setNewEdificacao({
                  ...newEdificacao,
                  lng: val,
                });
              }}
              variant="standard"
            />

            {/* Alt */}
            <TextField
              fullWidth
              label="Altitude"
              name="alt"
              value={(newEdificacao.alt ?? "").toString().replaceAll(".", ",")}
              onChange={(event) => {
                const strVal = event.target.value.replaceAll(",", ".");

                let val: number | string = strVal;
                if (strVal !== "" && strVal !== "-" && !strVal.endsWith(".")) {
                  val = Number(strVal);
                  if (isNaN(val)) return;
                }
                setNewEdificacao({
                  ...newEdificacao,
                  alt: val,
                });
              }}
              variant="standard"
            />

            {/* Style */}
            <Tooltip title={"Estilo dp Ícone"}>
              <StyleButton
                initialStyle={newEdificacao.style}
                onChange={(style) => setNewEdificacao({ ...newEdificacao, style })}
                initialType="Marker"
              />
            </Tooltip>

            {/* BTN Add */}
            <Tooltip title={"Adicionar Local"}>
              <IconButton onClick={handleAddEdificacao}>
                <Add />
              </IconButton>
            </Tooltip>

          </Box>
        }

        {/* Filtro pelo nome */}
        {isFiltrable &&
          <TextField
            fullWidth
            label="Nome"
            name="nomeFilter"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            variant="standard"
            InputProps={{
              endAdornment: <InputAdornment position="end">
                <Search />
              </InputAdornment>,
            }}
          />
        }
      </Box>

      <div style={{ height: "15px" }} />
      <Divider />
      <div style={{ height: "15px" }} />
    </div>

    {/* Tabela de estações */}
    <TableContainer sx={{
      flex: 1,
      overflow: "auto",
      height: size.height - filterSize.height - titleSize.height,
      width: size.width
    }}>
      <Table
        stickyHeader
        aria-label="sticky table"
        size="small"
      >
        <TableHead>
          <TableRow>
            {onChoose && <TableCell align="left">Selecionar</TableCell>}
            <TableCell align="left">Nome</TableCell>
            <TableCell align="left">Latitude</TableCell>
            <TableCell align="left">Longitude</TableCell>
            <TableCell align="center">Marcador</TableCell>
            {isEditable && <TableCell align="center">Remover</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {edificacoes.map((row, i) => (
            <TableRow
              key={i}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              {onChoose && <TableCell align="left">
                <IconButton onClick={() => onChoose(row)} >
                  <Start />
                </IconButton>
              </TableCell>}
              <TableCell align="left" sx={{ textOverflow: "ellipsis" }} >{row.properties.nome}</TableCell>
              <TableCell align="left" sx={{ textOverflow: "ellipsis" }} >{row.getLatLng().lat.toSignificantDigits("latStr")}</TableCell>
              <TableCell align="left" sx={{ textOverflow: "ellipsis" }} >{row.getLatLng().lng.toSignificantDigits("lngStr")}</TableCell>
              <TableCell align="center" sx={{ textOverflow: "ellipsis" }} >
                {isEditable ?
                  <StyleButton
                    initialStyle={row.properties.style}
                    initialType="Marker"
                    onChange={(style) => {
                      edificacoes[i].properties.style = style;
                      setEdificacoes(edificacoes);
                    }}
                  /> :
                  <IconFromStyle {...row.properties.style} />}
              </TableCell >
              <TableCell align="center" sx={{ textOverflow: "ellipsis" }}>
                <RemoveButton
                  title="Remover Unidade Sensível?"
                  message="Deseja mesmo remover esta unidade sensível? Esta ação é irreversível!"
                  onRemove={() => onRemove(row)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>;
};